import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { PageHeader } from '@/components/ds'
import { Skeleton } from '@/components/ui/skeleton'
import { StorefrontTester } from '@/containers/StorefrontTester/StorefrontTester'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import i18n from '@/i18n'
import { StorefrontTokenCreated } from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { createStorefrontClient } from '@/lib/eshop/storefrontApi'

const STORAGE_KEY = 'eshop.storefront-tester.token'

const readStoredToken = (): StorefrontTokenCreated | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StorefrontTokenCreated>
    if (
      typeof parsed.id === 'string' &&
      typeof parsed.token === 'string' &&
      typeof parsed.name === 'string'
    ) {
      return {
        id: parsed.id,
        name: parsed.name,
        token: parsed.token,
      }
    }
  } catch {
    // ignore invalid storage
  }

  return null
}

const writeStoredToken = (token: StorefrontTokenCreated) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(token))
}

// Deduplicate concurrent mints (React StrictMode remounts, fast navigations).
let pendingMint: Promise<StorefrontTokenCreated> | null = null

const ensureTesterToken = async (): Promise<StorefrontTokenCreated> => {
  const stored = readStoredToken()
  if (stored) return stored

  if (!pendingMint) {
    pendingMint = API.post<StorefrontTokenCreated>('/api/v2/storefront-tokens', {
      name: 'Storefront tester',
    })
      .then((response) => {
        writeStoredToken(response.data)
        return response.data
      })
      .finally(() => {
        pendingMint = null
      })
  }

  return pendingMint
}

export default function EshopTesterPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })

  const [token, setToken] = useState<StorefrontTokenCreated | null>(() =>
    readStoredToken(),
  )
  const [error, setError] = useState(false)

  useEffect(() => {
    if (token) return

    let ignore = false

    ensureTesterToken()
      .then((created) => {
        if (!ignore) setToken(created)
      })
      .catch((err: unknown) => {
        if (ignore) return
        setError(true)
        toast.error(i18n.t('eshop.tester.errors.tokenCreate'), {
          description: extractErrorMessage(err),
        })
      })

    return () => {
      ignore = true
    }
  }, [token])

  const client = useMemo(
    () => (token ? createStorefrontClient(token.token) : null),
    [token],
  )

  useSetPageHeader({
    breadcrumbs: [
      { href: '/eshop/tokens', children: t('title') },
      { href: '/eshop/tester', children: t('tester.title') },
    ],
  })

  return (
    <>
      <title>{t('title') + ', ' + t('tester.title')}</title>

      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader title={t('tester.title')} description={t('tester.description')} />

        {!client && !error && (
          <div className="space-y-4">
            <Skeleton className="h-7 w-96 rounded-full" />
            <Skeleton className="h-48 w-full rounded-lg" />
          </div>
        )}
        {error && (
          <div className="rounded-lg bg-danger-bg px-4 py-3 text-sm text-danger">
            {t('tester.errors.tokenCreate')}
          </div>
        )}
        {client && <StorefrontTester client={client} />}
      </div>
    </>
  )
}
